import React from "react";
import { formatDistanceToNow } from "date-fns";
import "./comment.scss";
import replyIcon from "../../../assets/icons/corner-up-right.svg";

export interface CommentAuthor {
  id: string;
  name: string;
  avatarUrl?: string;
}

export interface CommentType {
  id: string;
  author: CommentAuthor;
  html: string;
  timestamp: string | number | Date;
  replies?: CommentType[];
}

interface CommentProps {
  comment: CommentType;
  onReply: (author: CommentAuthor, parentId: string | null) => void;
  isReply?: boolean;
  canWrite?: boolean;
}

/**
 * Comment renders a single threaded comment with optional nested replies.
 * Supports reply action via onReply and disables controls with canWrite.
 */
const Comment: React.FC<CommentProps> = ({
  comment,
  onReply,
  isReply = false,
  canWrite = true,
}) => {
  return (
    <div className={`comment ${isReply ? "comment--reply" : ""}`}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div className="comment__avatar">
          {comment?.author?.avatarUrl ? (
            <img
              src={comment?.author?.avatarUrl || ""}
              alt={comment?.author?.name || "Avatar"}
              className="comment__avatar-img"
            />
          ) : (
            <div className="comment__avatar-fallback">
              {comment.author?.name.charAt(0).toUpperCase() || "A"}
            </div>
          )}
        </div>
        <div className="comment__header">
          <span className="comment__author">{comment.author?.name}</span>
          <span className="comment__timestamp">
            {comment.timestamp
              ? (() => {
                  const ts = comment.timestamp.toString().replace("UTC ", "");
                  const date = new Date(ts.endsWith("Z") ? ts : ts + "Z");
                  return Number.isNaN(date.getTime())
                    ? "Invalid date"
                    : formatDistanceToNow(date, { addSuffix: true });
                })()
              : "Unknown time"}
          </span>
        </div>
      </div>

      <div className="comment__body">
        <div
          className="comment__content"
          dangerouslySetInnerHTML={{ __html: comment.html }}
        />

        {canWrite && (
          <button
            type="button"
            className="comment__reply-btn"
            onClick={() => onReply(comment.author, comment.id)}
          >
            <img
              src={replyIcon}
              alt="Reply"
              className="comment__reply-icon"
            />{" "}
            Reply
          </button>
        )}

        {comment.replies && comment.replies.length > 0 && (
          <div className="comment__replies">
            {comment.replies.map((reply) => (
              <Comment
                key={reply.id}
                comment={reply}
                onReply={(author, parentId) =>
                  onReply(author, parentId || comment.id)
                }
                isReply={true}
                canWrite={canWrite}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Comment;
